/**
 * useInterfaceMode — Controls the visible complexity layer of the UI.
 *
 * 3 modes: simple (minimal), core (full product), advanced (core + optional depth).
 * Standard mode is locked until user creates 1 session or 1 program.
 * Advanced mode is locked until the user demonstrates sufficient usage.
 *
 * @since Progressive Interface System v2
 */

import { useCallback, useMemo } from "react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import type { UserIntent } from "@/hooks/useUserIntentProfile";
import { isSectionVisible, getSectionDepth } from "@/lib/sectionVisibility";
import { useAdvancedUnlock } from "@/hooks/useAdvancedUnlock";
import { useStandardUnlock } from "@/hooks/useStandardUnlock";

// ── Types ──────────────────────────────────────────

export type InterfaceMode = "simple" | "core" | "advanced";

const MODE_ORDER: Record<InterfaceMode, number> = {
  simple: 0,
  core: 1,
  advanced: 2,
};

// ── Intent-aware simple mode config ────────────────
const SIMPLE_ALWAYS_VISIBLE = ["/dashboard", "/profile", "/guide"];

const SIMPLE_INTENT_ROUTES: Partial<Record<string, string[]>> = {
  quick_session: ["/sessions"],
  structured_program: ["/programs", "/planning"],
  understand_training: ["/sessions", "/library"],
  track_progress: ["/sessions"],
  explore_platform: ["/programs", "/sessions"],
  create_session: ["/sessions"],
  follow_program: ["/programs", "/planning"],
  learn: ["/sessions", "/library"],
  explore: ["/programs", "/sessions"],
};

// ── Query keys ─────────────────────────────────────

const modeKeys = {
  current: (userId: string | null) => ["interface-mode", userId] as const,
};

// ── Hook ───────────────────────────────────────────

export function useInterfaceMode() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { isUnlocked: isAdvancedUnlocked } = useAdvancedUnlock();
  const { isUnlocked: isStandardUnlocked } = useStandardUnlock();

  const { data: mode = "simple" } = useQuery({
    queryKey: [...modeKeys.current(user?.id ?? null), isAdvancedUnlocked, isStandardUnlocked] as const,
    queryFn: async (): Promise<InterfaceMode> => {
      if (!user) return "simple";
      const { data } = await supabase
        .from("profiles")
        .select("interface_mode")
        .eq("user_id", user.id)
        .maybeSingle();
      const stored = (data as any)?.interface_mode as InterfaceMode;
      // Central enforcement: downgrade if locked
      if (stored === "advanced" && !isAdvancedUnlocked) {
        return isStandardUnlocked ? "core" : "simple";
      }
      if (stored === "core" && !isStandardUnlocked) return "simple";
      return stored ?? "simple";
    },
    enabled: !!user,
    staleTime: 5 * 60_000,
  });

  const { mutate: setMode } = useMutation({
    mutationFn: async (newMode: InterfaceMode) => {
      if (!user) return;
      // ── Central standard lock ──
      if (newMode === "core" && !isStandardUnlocked) {
        throw new Error("STANDARD_LOCKED");
      }
      // ── Central advanced lock ──
      if (newMode === "advanced" && !isAdvancedUnlocked) {
        throw new Error("ADVANCED_LOCKED");
      }
      const { error } = await supabase
        .from("profiles")
        .update({ interface_mode: newMode } as any)
        .eq("user_id", user.id);
      if (error) throw error;
    },
    onSuccess: (_, newMode) => {
      queryClient.setQueryData(modeKeys.current(user?.id ?? null), newMode);
      queryClient.invalidateQueries({ queryKey: modeKeys.current(user?.id ?? null) });
      const labels: Record<InterfaceMode, string> = {
        simple: "Simple",
        core: "Standard",
        advanced: "Avancé",
      };
      toast.success(`Mode ${labels[newMode]} activé — rechargement…`);
      setTimeout(() => window.location.reload(), 400);
    },
    onError: (err: Error) => {
      if (err.message === "ADVANCED_LOCKED") {
        toast.error("Le mode Avancé n'est pas encore débloqué. Complète les étapes requises.");
      } else if (err.message === "STANDARD_LOCKED") {
        toast.error("Crée au moins une séance ou un programme pour débloquer le mode Standard.");
      } else {
        toast.error("Erreur lors du changement de mode");
      }
    },
  });

  const isVisible = useCallback(
    (minMode: InterfaceMode): boolean => {
      return MODE_ORDER[mode] >= MODE_ORDER[minMode];
    },
    [mode]
  );

  const isRouteVisibleForIntent = useCallback(
    (route: string, userIntent: UserIntent | null): boolean => {
      if (MODE_ORDER[mode] >= MODE_ORDER["core"]) return true;
      if (SIMPLE_ALWAYS_VISIBLE.some((r) => route.startsWith(r))) return true;
      if (userIntent && SIMPLE_INTENT_ROUTES[userIntent]) {
        return SIMPLE_INTENT_ROUTES[userIntent]!.some((r) => route.startsWith(r));
      }
      return false;
    },
    [mode]
  );

  const isSectionVisibleForMode = useCallback(
    (sectionId: string, userIntent?: UserIntent | null, primaryGoal?: import("@/hooks/useUserIntentProfile").SimpleGoal | null): boolean => {
      return isSectionVisible(sectionId, mode, userIntent, primaryGoal);
    },
    [mode]
  );

  const getDepthVariant = useCallback(
    (sectionId: string): string | undefined => {
      return getSectionDepth(sectionId, mode);
    },
    [mode]
  );

  return {
    mode,
    setMode,
    isVisible,
    isRouteVisibleForIntent,
    isSectionVisibleForMode,
    getDepthVariant,
    isStandardUnlocked,
    isAdvancedUnlocked,
  };
}
